import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Chip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DoneIcon from "@mui/icons-material/Done";
import DropDown from "../../../components/common/Dropdown/Dropdown";
import { useClientDetails } from "../../../hooks/useClientDetails";
import { getAccountByClientId } from "../../../services/transactionService";
import {
  currentMonth,
  currentYear,
  getMonthOptions,
  YEARS,
} from "../../../variables/constants";
import "./styles.scss";
import CustomSnackbar from "../../../components/common/Snackbar/CustomSnackbar";
import { apiEndPoints } from "../../../variables/apiEndpoints";
import useFileDownloader from "../../../hooks/useFileDownloader";
import AutoCompleteSelect from "../../../components/common/autocompleteSelect/AutoCompleteSelect";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";

const ClientStatements = () => {
  const { role } = useSelector((state: RootState) => state.userDetails);
  const administratorView = role === "Administrator";

  const FROM_CLIENT_DEFAULT = 'All';
  const { clientOptions } = useClientDetails();
  const [accountOptions, setAccountOptions] = useState<any[]>([]);
  const [account, setAccount] = useState<any>("0");
  const [accountStatus, setAccountStatus] = useState<string>("open");
  const [investorStatus, setInvestorStatus] = useState<string>("active");
  const [accountType, setAccountType] = useState<string>("all");
  const { downloadFile } = useFileDownloader();
  const [email, setEmail] = useState<boolean>(false);
  const [fromClientId, setFromClientId] = React.useState(FROM_CLIENT_DEFAULT);
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);
  const [isLoading, setIsLoading] = useState(false);
  const [onSuccess, setOnSuccess] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.checked);
  };

  useEffect(() => {
    setAccount("0");
    if (fromClientId && fromClientId !== FROM_CLIENT_DEFAULT) {
      setAccountOptions([]);
      getAccountByClientId(fromClientId)
        ?.then((res: any) => {
          const accounts: any[] = [];
          res?.forEach((acc: any) => {
            if (acc.isActive) {
              accounts.push({ value: acc.id, label: `${acc.name} - ${acc.accountNumber}` });
            }
          });
          setAccountOptions(accounts);
        })
        .catch((err: any) => {
          console.log(err);
        });
    } else {
      setFromClientId(FROM_CLIENT_DEFAULT);
    }
  }, [fromClientId]);

  const handleDownload = useCallback(async () => {
    setIsLoading(true);
    const body = {
      client: {
        filter: fromClientId === "All" ? [] : [fromClientId],
        isActive: investorStatus === "active",
      },
      account: {
        filter: account === "0" ? [] : [account],
        isActive: accountStatus === "open",
      },
      month: selectedMonth,
      year: selectedYear,
      email: email,
      ...(accountType !== "all" ? { accountType } : {}),
    };
    const result = await downloadFile({
      url: `/reports${apiEndPoints.accounts}`,
      method: "post",
      fileName: "ClientAccountStatements.pdf",
      data: body,
    });
    setIsLoading(false);
    if (!result.success) {
      setSnackbarMsg(result.errorMessage || "Error downloading file");
      setOnSuccess(true);
    }
  }, [downloadFile, accountType]);

  return (
    <>
      <Toolbar className="toolbar">
        <Typography className="title">Client Account Statements</Typography>
      </Toolbar>
      <Paper className="container pd-20">
        <Stack spacing={3}>
          <Typography variant="h5" component="h3">
            Create Client Account Statement
          </Typography>
          <Box className="form-group">
            <AutoCompleteSelect
              items={[{ value: "All", label: "All Clients" }, ...clientOptions]}
              onSelectItem={setFromClientId}
              selected={fromClientId?.toString() ?? ""}
              placeholder="Select an client"
              label="Client"
              defaultValue={{ label: "All Clients", value: "All" }}
              fullWidth
            />
            <DropDown
              val={investorStatus}
              setVal={setInvestorStatus}
              options={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
              label="Client Status"
            />
          </Box>
          <Box className="form-group">
            <AutoCompleteSelect
              items={[{ value: "0", label: "All Accounts" }, ...accountOptions]}
              onSelectItem={setAccount}
              selected={account?.toString() ?? ""}
              placeholder="Select an account"
              label="Account"
              defaultValue={{ label: "All Accounts", value: "0" }}
              fullWidth
            />
            <DropDown
              val={accountStatus}
              setVal={setAccountStatus}
              options={[
                { value: "open", label: "Open" },
                { value: "closed", label: "Closed" },
              ]}
              label="Account Status"
            />
          </Box>
          <Box className="form-group">
            <DropDown
              val={accountType}
              setVal={setAccountType}
              options={[
                { value: "all", label: "All" },
                { value: "investment", label: "Investment" },
                { value: "loan", label: "Loan" },
              ]}
              label="Account Type"
            />
            <FormControl className="dropdown-month" size="small">
              <InputLabel id="demo-simple-select-label">Month</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(e.target.value as number)}
              >
                {getMonthOptions().map((month) => (
                  <MenuItem
                    key={month.value}
                    value={month.value}
                    selected={month.default}
                  >
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className="dropdown-year" size="small">
              <InputLabel id="demo-simple-select-label">Year</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                label="Year"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value as number)}
              >
                {YEARS.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <FormGroup className="form-group"></FormGroup>

          {administratorView && <FormGroup className="form-group">
            <FormControlLabel
              className="checkobox-label"
              control={<Checkbox checked={email} onChange={handleChange} />}
              label="Send email"
            />
          </FormGroup>}

          <Divider className="divider" />
          <Box className="actions">
            <Box>
              <Typography variant="caption" component="p">
                Format
              </Typography>
              <Chip
                size="small"
                icon={<DoneIcon fontSize="small" />}
                color="primary"
                label="PDF"
              />
            </Box>
            <LoadingButton
              variant="contained"
              data-testid="createTxnBtn"
              size="small"
              onClick={handleDownload}
              loading={isLoading}
            >
              Print {email && "& Send"} STATEMENT(s)
            </LoadingButton>
          </Box>
        </Stack>
        <CustomSnackbar
          open={onSuccess}
          message={snackbarMsg}
          setOpen={setOnSuccess}
        />
      </Paper>
    </>
  );
};

export default ClientStatements;
